import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "@/lib/hooks/api/utils";
import type { Contact_ProposalsUpdateInput } from "@/src/gql/graphql";
import { Contact_ProposalsUpdateInputSchema } from "@/src/gql/validation";

export function useUpdateContactProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Contact_ProposalsUpdateInput }) => {
      const validatedData = Contact_ProposalsUpdateInputSchema.parse(updates);

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contact_proposals")
        .update(validatedData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Error updating contact proposal:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["contact-proposals"] });
      void queryClient.invalidateQueries({ queryKey: ["contact-proposals", data.id] });
      toast.success("Proposal updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update proposal");
      console.error("Error updating contact proposal:", error);
    },
  });
}

export function useDeleteContactProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const supabase = getSupabase();
      const { error } = await supabase.from("contact_proposals").delete().eq("id", id);

      if (error) {
        console.error("Error deleting contact proposal:", error);
        throw new Error(error.message);
      }

      return { id, deleted: true };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contact-proposals"] });
      toast.success("Proposal deleted successfully");
    },
    onError: (error) => {
      toast.error("Failed to delete proposal");
      console.error("Error deleting contact proposal:", error);
    },
  });
}

// Backward compatibility alias
export function useDeleteContractProposal() {
  return useDeleteContactProposal();
}
